import React, { useState, useEffect } from 'react';
import { Spinner, Alert, Modal, Button } from 'react-bootstrap';
import supabase from '../supabase';

function buildMonths(latestDateStr) {
  // Build the 3 most recent months ending at the month of latestDateStr
  const [year, month] = latestDateStr.slice(0, 7).split('-').map(Number);
  const months = [];
  for (let i = 2; i >= 0; i--) {
    const d = new Date(year, month - 1 - i, 1);
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    months.push({
      year: y, month: m,
      label: d.toLocaleString('default', { month: 'long', year: 'numeric' }),
      short: d.toLocaleString('default', { month: 'short' }),
      key: `${y}-${String(m).padStart(2, '0')}`,
    });
  }
  return months;
}

const emptyForm = (category, monthKey) => ({
  date: monthKey ? `${monthKey}-01` : '',
  amount: '',
  category: category || '',
  description: '',
  reference: '',
});

export default function Transactions() {
  const [rows, setRows] = useState({ expenses: [], income: [] });
  const [allTx, setAllTx] = useState([]);
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null); // { category, monthKey }
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [addingNew, setAddingNew] = useState(false);
  const [newForm, setNewForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  async function fetchTransactions() {
    // Step 1: find the most recent transaction date
    const { data: latest, error: latestErr } = await supabase
      .from('transactions')
      .select('date')
      .order('date', { ascending: false })
      .limit(1);

    if (latestErr) { setError(latestErr.message); setLoading(false); return; }
    if (!latest || latest.length === 0) { setLoading(false); return; }

    const computedMonths = buildMonths(latest[0].date);
    setMonths(computedMonths);

    // Step 2: fetch only those 3 months
    const oldest = `${computedMonths[0].key}-01`;
    const last = computedMonths[computedMonths.length - 1];
    const lastDay = new Date(last.year, last.month, 0).getDate();
    const newest = `${last.key}-${String(lastDay).padStart(2, '0')}`;

    const { data, error } = await supabase
      .from('transactions')
      .select('id, date, amount, category, description, card_member, reference')
      .gte('date', oldest)
      .lte('date', newest)
      .order('date', { ascending: true });

    if (error) { setError(error.message); setLoading(false); return; }

    setAllTx(data || []);

    const summary = {};
    for (const tx of (data || [])) {
      const cat = tx.category || 'Uncategorized';
      const monthKey = tx.date?.slice(0, 7);
      if (!monthKey) continue;
      if (!summary[cat]) summary[cat] = {};
      summary[cat][monthKey] = (summary[cat][monthKey] || 0) + Number(tx.amount || 0);
    }

    const sorted = Object.keys(summary)
      .sort((a, b) => a.localeCompare(b))
      .map(cat => ({ category: cat, totals: summary[cat] }));

    // Split income vs expenses
    const nonExpense = (cat) => ['income', 'assets', 'taxes'].includes(cat.toLowerCase());
    const expenses = sorted.filter(r => !nonExpense(r.category));
    const income = sorted.filter(r => nonExpense(r.category));
    setRows({ expenses, income });
    setLoading(false);
  }

  useEffect(() => { fetchTransactions(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fmt = (val) =>
    val != null && val !== 0
      ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val)
      : '—';

  const fmtFull = (val) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);

  const monthTotal = (monthKey) =>
    rows.expenses.reduce((sum, r) => sum + (r.totals[monthKey] || 0), 0);

  const detailTx = detail
    ? allTx.filter(tx => {
        const cat = tx.category || 'Uncategorized';
        return cat === detail.category && tx.date?.startsWith(detail.monthKey);
      }).sort((a, b) => a.date.localeCompare(b.date))
    : [];

  const detailMonthLabel = detail
    ? months.find(m => m.key === detail.monthKey)?.label || detail.monthKey
    : '';

  function startEdit(tx) {
    setEditingId(tx.id);
    setEditForm({ category: tx.category || '', description: tx.description || '' });
    setAddingNew(false);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditForm({});
  }

  async function saveEdit(id) {
    setSaving(true);
    const { error } = await supabase
      .from('transactions')
      .update({
        category: editForm.category,
        description: editForm.description,
      })
      .eq('id', id);
    if (error) { setError(error.message); setSaving(false); return; }
    setEditingId(null);
    await fetchTransactions();
    setSaving(false);
  }

  async function confirmDelete(id) {
    setSaving(true);
    const { error } = await supabase.from('transactions').delete().eq('id', id);
    if (error) { setError(error.message); setSaving(false); return; }
    setDeleteConfirmId(null);
    await fetchTransactions();
    setSaving(false);
  }

  function startAdd() {
    setAddingNew(true);
    setNewForm(emptyForm(detail.category, detail.monthKey));
    setEditingId(null);
  }

  function cancelAdd() {
    setAddingNew(false);
    setNewForm({});
  }

  async function saveNew() {
    if (!newForm.date || !newForm.amount || !newForm.reference) return;
    setSaving(true);
    const { error } = await supabase.from('transactions').insert({
      date: newForm.date,
      amount: parseFloat(newForm.amount),
      category: newForm.category || 'Uncategorized',
      description: newForm.description,
      reference: newForm.reference,
    });
    if (error) { setError(error.message); setSaving(false); return; }
    setAddingNew(false);
    await fetchTransactions();
    setSaving(false);
  }

  function closeModal() {
    setDetail(null);
    setEditingId(null);
    setAddingNew(false);
    setDeleteConfirmId(null);
  }

  if (loading) return <div className="text-center mt-5"><Spinner animation="border" /></div>;

  return (
    <div>
      <div className="page-header">
        <h1>Expenses</h1>
        <p>Spending by category — {months.map(m => m.short).join(', ')}</p>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <div className="d-card" style={{ overflowX: 'auto' }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={{ ...thStyle, textAlign: 'left' }}>Category</th>
              {months.map(m => <th key={m.key} style={thStyle}>{m.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.expenses.length === 0 ? (
              <tr>
                <td colSpan={months.length + 1} style={{ ...tdStyle, color: 'var(--text-muted)', textAlign: 'center' }}>
                  No transactions found.
                </td>
              </tr>
            ) : rows.expenses.map(row => (
              <tr key={row.category}>
                <td style={{ ...tdStyle, textAlign: 'left', fontWeight: 500 }}>{row.category}</td>
                {months.map(m => (
                  <td key={m.key} style={tdStyle}>
                    {row.totals[m.key] ? (
                      <button style={linkBtnStyle} onClick={() => setDetail({ category: row.category, monthKey: m.key })}>
                        {fmt(row.totals[m.key])}
                      </button>
                    ) : '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '2px solid var(--border, #333)' }}>
              <td style={{ ...tdStyle, textAlign: 'left', fontWeight: 700 }}>Total</td>
              {months.map(m => (
                <td key={m.key} style={{ ...tdStyle, fontWeight: 700 }}>{fmt(monthTotal(m.key))}</td>
              ))}
            </tr>
            {rows.income.length > 0 && (
              <>
                <tr>
                  <td colSpan={months.length + 1} style={{ ...tdStyle, textAlign: 'left', color: 'var(--text-muted)', fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase', paddingTop: '1rem' }}>
                    Income, Assets & Taxes
                  </td>
                </tr>
                {rows.income.map(row => (
                  <tr key={row.category}>
                    <td style={{ ...tdStyle, textAlign: 'left', fontWeight: 500 }}>{row.category}</td>
                    {months.map(m => (
                      <td key={m.key} style={tdStyle}>
                        {row.totals[m.key] ? (
                          <button style={{ ...linkBtnStyle, color: 'var(--success, #4caf50)' }} onClick={() => setDetail({ category: row.category, monthKey: m.key })}>
                            {fmt(row.totals[m.key])}
                          </button>
                        ) : '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </>
            )}
          </tfoot>
        </table>
      </div>

      {/* Detail / Edit Modal */}
      <Modal show={!!detail} onHide={closeModal} centered size="lg">
        <Modal.Header closeButton style={modalHeaderStyle}>
          <Modal.Title style={{ color: 'var(--text)', fontSize: '1rem' }}>
            {detail?.category} — {detailMonthLabel}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: 'var(--bg-card, #1a1a1a)', padding: 0 }}>
          <table style={{ ...tableStyle, margin: 0 }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, textAlign: 'left' }}>Date</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Description</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Card / Reference</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Category</th>
                <th style={thStyle}>Amount</th>
                <th style={{ ...thStyle, textAlign: 'center', width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {detailTx.length === 0 && !addingNew ? (
                <tr>
                  <td colSpan={4} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-muted)' }}>
                    No transactions.
                  </td>
                </tr>
              ) : detailTx.map(tx => (
                <tr key={tx.id}>
                  {editingId === tx.id ? (
                    <>
                      <td style={{ ...tdStyle, textAlign: 'left', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>{tx.date}</td>
                      <td style={tdStyle}>
                        <input style={inputStyle} type="text" value={editForm.description}
                          placeholder="Description"
                          onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'left', color: 'var(--text-muted)', fontSize: '0.8rem' }}>{tx.card_member || '—'}</td>
                      <td style={tdStyle}>
                        <input style={inputStyle} type="text" value={editForm.category}
                          placeholder="Category"
                          onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))} />
                      </td>
                      <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>{fmtFull(tx.amount)}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <button style={iconBtn} onClick={() => saveEdit(tx.id)} disabled={saving} title="Save">✓</button>
                        <button style={{ ...iconBtn, color: 'var(--text-muted)' }} onClick={cancelEdit} title="Cancel">✕</button>
                      </td>
                    </>
                  ) : deleteConfirmId === tx.id ? (
                    <>
                      <td colSpan={5} style={{ ...tdStyle, textAlign: 'left', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                        Delete "{tx.description || tx.date}"?
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <button style={{ ...iconBtn, color: '#e55' }} onClick={() => confirmDelete(tx.id)} disabled={saving} title="Confirm delete">✓</button>
                        <button style={{ ...iconBtn, color: 'var(--text-muted)' }} onClick={() => setDeleteConfirmId(null)} title="Cancel">✕</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={{ ...tdStyle, textAlign: 'left', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>{tx.date}</td>
                      <td style={{ ...tdStyle, textAlign: 'left' }}>{tx.description || '—'}</td>
                      <td style={{ ...tdStyle, textAlign: 'left', color: 'var(--text-muted)', fontSize: '0.8rem' }}>{tx.card_member || '—'}</td>
                      <td style={{ ...tdStyle, textAlign: 'left' }}>{tx.category || '—'}</td>
                      <td style={tdStyle}>{fmtFull(tx.amount)}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <button style={iconBtn} onClick={() => startEdit(tx)} title="Edit">✎</button>
                        <button style={{ ...iconBtn, color: '#e55' }} onClick={() => setDeleteConfirmId(tx.id)} title="Delete">🗑</button>
                      </td>
                    </>
                  )}
                </tr>
              ))}

              {/* New row */}
              {addingNew && (
                <tr style={{ background: 'rgba(124,140,248,0.06)' }}>
                  <td style={tdStyle}>
                    <input style={inputStyle} type="date" value={newForm.date}
                      onChange={e => setNewForm(f => ({ ...f, date: e.target.value }))} />
                  </td>
                  <td style={tdStyle}>
                    <input style={inputStyle} type="text" value={newForm.description}
                      placeholder="Description"
                      onChange={e => setNewForm(f => ({ ...f, description: e.target.value }))} />
                  </td>
                  <td style={tdStyle}>
                    <input style={inputStyle} type="text" value={newForm.reference}
                      placeholder="Reference *"
                      onChange={e => setNewForm(f => ({ ...f, reference: e.target.value }))} />
                  </td>
                  <td style={tdStyle}>
                    <input style={inputStyle} type="text" value={newForm.category}
                      placeholder="Category"
                      onChange={e => setNewForm(f => ({ ...f, category: e.target.value }))} />
                  </td>
                  <td style={tdStyle}>
                    <input style={{ ...inputStyle, textAlign: 'right', width: 90 }} type="number"
                      value={newForm.amount} step="0.01" placeholder="0.00"
                      onChange={e => setNewForm(f => ({ ...f, amount: e.target.value }))} />
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button style={iconBtn} onClick={saveNew} disabled={saving || !newForm.reference} title="Save">✓</button>
                    <button style={{ ...iconBtn, color: 'var(--text-muted)' }} onClick={cancelAdd} title="Cancel">✕</button>
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid var(--border, #333)' }}>
                <td colSpan={4} style={{ ...tdStyle, textAlign: 'left', fontWeight: 700, borderTop: '2px solid var(--border, #333)' }}>Total</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>
                  {fmtFull(detailTx.reduce((s, tx) => s + Number(tx.amount || 0), 0))}
                </td>
                <td style={tdStyle}></td>
              </tr>
            </tfoot>
          </table>
        </Modal.Body>
        <Modal.Footer style={{ ...modalHeaderStyle, justifyContent: 'flex-start' }}>
          <Button variant="outline-secondary" size="sm" onClick={addingNew ? cancelAdd : startAdd}>
            {addingNew ? 'Cancel add' : '+ Add transaction'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' };

const thStyle = {
  padding: '0.6rem 1rem',
  textAlign: 'right',
  color: 'var(--text-muted)',
  fontWeight: 600,
  fontSize: '0.78rem',
  borderBottom: '1px solid var(--border, #333)',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '0.45rem 1rem',
  textAlign: 'right',
  borderBottom: '1px solid var(--border, #222)',
  color: 'var(--text)',
};

const modalHeaderStyle = {
  background: 'var(--bg-card, #1a1a1a)',
  borderBottom: '1px solid var(--border, #333)',
  borderTop: '1px solid var(--border, #333)',
};

const linkBtnStyle = {
  background: 'none', border: 'none', padding: 0,
  color: 'var(--accent, #7c8cf8)', cursor: 'pointer',
  fontWeight: 500, fontSize: 'inherit',
};

const iconBtn = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--accent, #7c8cf8)', fontSize: '1rem', padding: '0 4px',
};

const inputStyle = {
  background: 'var(--bg, #111)',
  border: '1px solid var(--border, #444)',
  borderRadius: 4,
  color: 'var(--text)',
  padding: '2px 6px',
  fontSize: '0.85rem',
  width: '100%',
};
